import classes from "./provider_item.module.css";
import ChevronRight from '@/assets/svg/icons/chevron-right.svg';
import ChevronDown from '@/assets/svg/icons/chevron-down.svg';
import ChevronUp from '@/assets/svg/icons/chevron-up.svg';
import MapPin from '@/assets/svg/icons/map-pin.svg';
import PlusCircle from '@/assets/svg/icons/plus-circle.svg';
import MinusCircle from '@/assets/svg/icons/minus-circle.svg';
import CustomSVG from "../common/svg";
import CustomButton from "../common/buttons/button";
import CustomBadge from "../common/badges/badge";
import CheckSquare from '@/assets/svg/icons/check-square.svg';
import { getProviderCategoryIcon, getProviderStatuses } from "@/variables/variables";
import { forwardRef, useState } from "react";
import { Dropdown } from "react-bootstrap";
import { createPortal } from "react-dom";
import Check from '@/assets/svg/icons/check.svg';
import X from '@/assets/svg/icons/x.svg';
import { useParams } from "next/navigation";
import { useMutation } from "@tanstack/react-query";
import { changeInlineProvider } from "@/utils/http";
import SafeImage from "@/components/common/safe_image";
import EmptyProviderLogo from '@/assets/png/empty-event-logo.png';
// import "slick-carousel/slick/slick.css";
// import "slick-carousel/slick/slick-theme.css";
// import Slider from "react-slick";
// import "./slider.css";

const provider_statuses = getProviderStatuses();

const CustomToggle = forwardRef(function CustomToggle({ onClick, children }, ref) {
    return (
        <div ref={ref} className="d-flex align-items-center pointer" onClick={(e) => {e.preventDefault(); e.stopPropagation(); onClick(e);}}>
            {children}
        </div>
    )
});

export default function ProviderItem({provider, setProviders, createProviderInfo, setCreateProviderInfo, compact = false}) {
    const params = useParams();
    
    const { mutate: changeInline } = useMutation({
        mutationFn: changeInlineProvider
    });

    const openProvider = () => {
        setCreateProviderInfo(prev => ({
            ...prev,
            id: provider.id, 
            active: true,
            read_only: true,
            custom: provider.is_custom,
            is_archiv: (provider.status === "archiv")
        }))
    }

    const openOffer = (e, id) => {
        e.preventDefault();
        e.stopPropagation();

        setCreateProviderInfo(prev => ({
            ...prev,
            id: provider.id, 
            active: true,
            read_only: true,
            custom: provider.is_custom,
            is_archiv: (provider.status === "archiv"),
            initial_message: true
        }));
    }

    const statusChangeHandler = (e, id, field, val) => {
        e.preventDefault();
        e.stopPropagation();
        
        setProviders(prev => {
            let temp_prev = [...prev];
            const prev_index = prev.findIndex(p => p.id === id);

            if(prev_index === -1) return prev;
            
            if(field === "status") {
                temp_prev[prev_index].status = val;
            } else if(field === "progress_status_id") {
                temp_prev[prev_index].progress_status = temp_prev[prev_index].progress_status.map(p => ({...p, is_active: p.id === val}));
            }

            return temp_prev;
        })

        changeInline({provider_id: id, wedding_id: params["wedding_id"], field: field, val: val})
    }
    
    const active_progress_status_index = provider.progress_status.findIndex(p => p.is_active);
    const active_progress_status = provider.progress_status[active_progress_status_index];
    const progressText = active_progress_status_index === 0 && !provider.contact_started
        ? "Kapcsolatfelvételre vár"
        : active_progress_status.name;

    let status_options = [];
    for(let p in provider_statuses) { 
        if(p !== "archiv") status_options.push({val: p, name: <ProviderItemBadge text={provider_statuses[p].name} color={provider_statuses[p].type} inner_svg={provider_statuses[p].icon} />})
    }

    let progress_options = [];
    for(let p in provider.progress_status) {
        progress_options.push({val: provider.progress_status[p].id, name: <ProviderItemBadge text={provider.progress_status[p].name} color={"success"} inner_svg={getProviderCategoryIcon(provider.progress_status[p].icon)} />})
    }

    return <div id={`provider-item-${provider.id}`} className={`${classes["provider-item-container"]} ${compact ? classes["compact"] : ""} ${createProviderInfo.id.toString() === provider.id.toString() ? classes["active"] : ""}`} onClick={openProvider}>
        <div className={`${classes["provider-item-image-container"]}`}>
            <ProviderImage id={provider.id} images={provider.images} is_custom={provider.is_custom} name={provider.name} city={provider.address_city ? provider.address_city : "-"} compact={compact} />
        </div>
        <div className={`${classes["provider-item-info-section"]} h-100`}>
            <div className={`d-flex flex-row gap-2 justify-content-between align-items-start align-self-stretch w-100 ${classes["provider-item-header"]}`}>
                <div className="d-flex flex-column align-items-start overflow-hidden flex-grow-1" style={{minWidth: 0}}>
                    <h3 className={`color-gray-800 text-lg-medium margin-0 ${classes["provider-name"]}`}>{provider.name}</h3>
                    <div className="d-flex flex-row gap-1 align-items-center">
                        <CustomSVG className="stroke-gray-500" SvgFunction={MapPin} height={14} width={14} />
                        <p className="margin-0 padding-0 color-gray-500">{provider.address_city ? provider.address_city : "-"}</p>
                    </div>
                </div>
                <div className="flex-shrink-0">
                    <CustomSVG className="stroke-gray-500" SvgFunction={ChevronRight} height={20} width={20} />
                </div>
            </div>
            <div className={`${classes["provider-item-info-container"]}`}>
                <div className={classes["provider-item-badge-row"]}>
                    <ProviderItemBadgeContainer title={"Státusz"} text={provider_statuses[provider.status].name} color={provider_statuses[provider.status].type} inner_svg={provider_statuses[provider.status].icon} selected={provider.status} options={provider.status === "archiv" ? undefined : status_options} selectHandler={(e, val) => statusChangeHandler(e, provider.id, "status", val)} />
                    <ProviderItemBadgeContainer title={"Előrehaladás"} text={provider.status === "archiv" ? "Megszakítva" : progressText} color={provider.status === "archiv" ? "error" : "success"} inner_svg={provider.status === "archiv" ? X : getProviderCategoryIcon(active_progress_status.icon)} selected={active_progress_status.id} options={provider.status === "archiv" ? undefined : progress_options} selectHandler={(e, val) => statusChangeHandler(e, provider.id, "progress_status_id", val)} />
                    {provider.new_messages_count > 0 && <ProviderItemBadgeContainer title={"Üzenet"} text={"Új üzenet"} color={"primary"} inner_badge_text={provider.new_messages_count} shade={"dark"} />}
                </div>
                {/*<div className="d-flex flex-row flex-wrap gap-4">
                    <ProviderItemBadgeContainer title={"Költségek"} text={"Költség"} color={"gray"} inner_badge_text={provider.budget_count} />
                    <ProviderItemBadgeContainer title={"Teendők"} text={"Teendő"} color={"gray"} inner_badge_text={provider.event_count} />
                    <ProviderItemBadgeContainer title={"Csatolmányok"} text={"Csatolmány"} color={"gray"} inner_badge_text={provider.attachment_count} />
                </div>*/}
                <div className={`d-flex flex-column align-items-start ${classes["provider-category-detail"]}`}>
                    <p className="margin-0 padding-0 text-sm-regular color-gray-500">Kategória</p>
                    <h3 className="margin-0 padding-0 color-gray-800 text-xs-medium">{provider.provider_type_name ? provider.provider_type_name : "-"}</h3>
                </div>
                {(!compact || (provider.note && provider.note !== "<p>-</p>")) && <div className="d-flex flex-column align-items-start align-self-stretch" >
                    <p className="margin-0 padding-0 text-sm-regular color-gray-500">Megjegyzés</p>
                    <h3 className={`margin-0 padding-0 color-gray-800 text-xs-medium w-100 ${classes["description"]}`} dangerouslySetInnerHTML={{__html: (provider.note ? provider.note : "<p>-</p>")}} />
                </div>}
            </div>
            <div className="mt-auto">
                {provider.status === "archiv"
                    ? <></>/*<CustomButton size="sm" hierarchy="secondary-gray" icon="leading" is_svg={true} svg={PlusCircle} onClick={e => statusChangeHandler(e, provider.id, "status", "like")} >Visszaállítás a listába</CustomButton>*/
                    : (provider.is_custom ? <></> : <CustomButton size="sm" hierarchy="primary" onClick={e => openOffer(e, provider.id)} >Ajánlatkérés</CustomButton>)/*<CustomButton size="sm" hierarchy="secondary-gray" icon="leading" is_svg={true} svg={MinusCircle} onClick={e => statusChangeHandler(e, provider.id, "status", "archiv")} >Archiválás a listából</CustomButton>*/
                }
            </div>
        </div>
    </div>
}

export function ProviderItemBadgeContainer({title, selected, options, text, inner_badge_text, inner_svg, color, shade = "light", selectHandler}) {
    const [isOpen, setIsOpen] = useState(false);

    const handleToggle = () => {setIsOpen(!isOpen)};

    const selectRowHandler = (e, val) => {
        selectHandler(e, val);
    }

    return <div className="d-flex flex-column align-items-start" >
        <p className="margin-0 padding-0 text-sm-regular color-gray-500">{title}</p>
        {options
            ? <Dropdown show={isOpen} onToggle={handleToggle}>
                <Dropdown.Toggle as={CustomToggle} active={isOpen} >
                    <ProviderItemBadge text={text} inner_badge_text={inner_badge_text} inner_svg={inner_svg} color={color} shade={shade} is_dropdown={true} is_active={isOpen} />
                </Dropdown.Toggle>
                {typeof document !== 'undefined' && createPortal(
                    <Dropdown.Menu align="start" className="margin-top-2" style={{zIndex: 9999}}>
                        {options.map(o => <Dropdown.Item key={`dropdown-option-${o.val}`} className="d-flex justify-content-between" onClick={(e) => selectRowHandler(e, o.val)} active={selected === o.val}>
                            {o.name}
                            {selected === o.val && <CustomSVG className="stroke-gray-900" SvgFunction={Check} height={20} width={20} />}
                        </Dropdown.Item>)}
                    </Dropdown.Menu>,
                    document.body
                )}
            </Dropdown>
            : <ProviderItemBadge text={text} inner_badge_text={inner_badge_text} inner_svg={inner_svg} color={color} shade={shade} is_dropdown={false} />
        }
    </div>
}

export function ProviderItemBadge({text, inner_badge_text, inner_svg, color, shade = "light", is_dropdown, is_active}) {
    return <CustomBadge  size={"md"} icon={is_dropdown ? "right" : false} is_svg={true} color={`${color}-parent-${shade}`} svg={is_active ? ChevronUp : ChevronDown} nested={"left"} >
        <CustomBadge size={"sm"} color={`${color}-child-${shade}`} icon={inner_svg ? "only" : false} is_svg={true} svg={inner_svg} >
            {inner_badge_text}
        </CustomBadge>
        {text}
    </CustomBadge>
}

function ProviderImage({id, images, is_custom, name, city, compact}) {
    let sliced_images = compact && images ? images.slice(0, 1) : images;
    if(!compact && images && images.length > 6) sliced_images = images.slice(0, 6)
    
    const [activeSlide, setActiveSlide] = useState(0);
    const sliderRef = useState(null); // Using state for ref callback or just useRef

    const handleScroll = (e) => {
        const slideWidth = e.target.offsetWidth;
        const scrollLeft = e.target.scrollLeft;
        const index = Math.round(scrollLeft / slideWidth);
        setActiveSlide(index);
    };

    return <div className={`${classes["provider-item-image"]} slick-provider-slider-container position-relative`}>
        {/*is_custom && <div className={`${classes["custom-badge-container"]}`}>
            <CustomBadge size="sm" color={"primary"} >Egyénileg létrehozva</CustomBadge>
        </div>*/}
        {(!sliced_images || sliced_images.length === 0) 
            ? <div className={classes["provider-empty-image"]}>
                {compact
                    ? <SafeImage sizes="56px" src={EmptyProviderLogo} alt="Nincs szolgáltatói kép" />
                    : <div className="d-flex flex-column align-items-center align-self-stretch padding-2 gap-4">
                    <SafeImage sizes={"auto"} src={EmptyProviderLogo} alt={"Szolgáltató képe"} />
                    <div className={classes["provider-empty-divider"]} />
                    <div className="d-flex flex-column align-items-center align-self-stretch gap-2">
                        {is_custom && <CustomBadge size="sm" color={"primary"} >Egyénileg létrehozva</CustomBadge>}
                        <p className="margin-0 color-primary-700 text-md-medium text-center w-100 text-truncate">{name}</p>
                        <div className="d-flex flex-row gap-1 align-items-center">
                            <CustomSVG className="stroke-primary-700 circle-stroke-primary-700" SvgFunction={MapPin} height={16} width={16} />
                            <p className="margin-0 color-primary-700">{city}</p>
                        </div>
                    </div>
                </div>}
            </div>
            : sliced_images.length === 1
                ? <div className={`${classes["img-container"]}`} >
                    <SafeImage src={sliced_images[0]} width={1000} height={1000} alt={`${id}-img`} />
                </div>
                : <>
                    <div className={classes["slider-container"]} onScroll={handleScroll}>
                        {sliced_images.map((img, index) => <div key={`${id}-img-${index}`} className={classes["slide"]}>
                            <div className={`${classes["img-container"]}`} >
                                <SafeImage src={img} width={1000} height={1000} alt={`${id}-img-${index}`} />
                            </div>
                        </div>)}
                    </div>
                    <div className={classes["dots-container"]}>
                        {sliced_images.map((_, index) => (
                            <div 
                                key={`dot-${index}`} 
                                className={`${classes["dot"]} ${activeSlide === index ? classes["active"] : ""}`}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    // Optional: Implement click to scroll
                                }}
                            />
                        ))}
                    </div>
                </>
        }
    </div>
}